import { filter } from 'rxjs/operators';

import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  WidgetEffectParams
} from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';
import type {
  GetFilteredClubInfoRequest,
  GetFilteredClubInfoResponse,
  GetUserClubsAndScheduledGamesResponse,
  GetUserClubsResponse,
  HgClubInfo
} from '@swf/generated/types/api/v0/SwHomeGames';
import type { HomeGamesWidgetModel } from '@swf/poker';
import { PKR_LOADERS } from '@swf/poker';

const effects = {
  async fetchInitialData(
    _: undefined,
    { request, dispatch, widget: { id } }: WidgetEffectParams<void>
  ): Promise<void> {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const { body } = await request<GetUserClubsAndScheduledGamesResponse>({
      service: 'homeGames.userClubsAndScheduledGamesRequest'
    });
    widgetDispatch.userClubsAndGames(body);
    widgetDispatch.loading({ isLoading: false, loaderName: PKR_LOADERS.initial });
  },

  async preload(_: undefined, params: WidgetEffectParams<void>): Promise<void> {
    const {
      actions,
      select,
      rootState,
      dispatch,
      widget: { id }
    } = params;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];

    if (isClientAuthenticated) {
      effects.fetchInitialData(undefined, params);
    }

    if (canUseDOM) {
      actions
        .pipe(
          filter(
            ({ type, state }) =>
              type === 'session/response' && select.session.isClientAuthenticated(state)
          )
        )
        .subscribe(async () => {
          widgetDispatch.loading({ isLoading: true, loaderName: PKR_LOADERS.initial });
          effects.fetchInitialData(undefined, params);
        });

      actions
        .pipe(
          filter(
            ({ type, state }) =>
              type === 'session/response' && !select.session.isClientAuthenticated(state)
          )
        )
        .subscribe(async () => {
          widgetDispatch.loading({ isLoading: false, loaderName: PKR_LOADERS.initial });
        });
    }
  },
  async getUserClubs(
    _: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const loaderName = PKR_LOADERS.userClubs;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    widgetDispatch.loading({ isLoading: true, loaderName });

    const { body } = await request<GetUserClubsResponse>({
      service: 'homeGames.userClubsRequest'
    });

    widgetDispatch.userClubs(body);
    widgetDispatch.loading({ isLoading: false, loaderName });
  },
  async getClubInfo(
    _: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel> = select[id];
    const loaderName = PKR_LOADERS.clubInfo;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    const { clubs } = widgetSelect.get(rootState);

    widgetDispatch.loading({ isLoading: true, loaderName });

    const {
      body: { filteredClubInfoReply }
    } = await request<GetFilteredClubInfoResponse>({
      service: 'homeGames.filteredClubInfoRequest',
      body: {
        gameFilter: {
          gameCategoryMask: 255,
          clubIds: clubs.map(({ clubId }: HgClubInfo) => clubId)
        } as GetFilteredClubInfoRequest
      }
    });

    widgetDispatch.userClubsAndGames({ clubInfoList: clubs, filteredClubInfoReply });
    widgetDispatch.loading({ isLoading: false, loaderName });
  }
};

export default effects;
